import type { SidebarItem } from "@/verticals/types";
import { go } from "@/shared/dashboard/shell/navigate";

const DESKTOP_APP_SESSION_KEY = "empatiq:desktop-app";
const DESKTOP_HINT_SEEN_KEY = "empatiq:desktop-hint-seen-v1";

export interface DesktopAppSession {
  id: string;
  label: string;
  icon: string;
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Keep the desktop frame active while an app moves through its own sub-routes. */
export function prepareDesktopApp(
  item: Pick<SidebarItem, "id" | "label" | "icon">,
): void {
  try {
    storage()?.setItem(
      DESKTOP_APP_SESSION_KEY,
      JSON.stringify({
        id: item.id,
        label: item.label,
        icon: item.icon,
      } satisfies DesktopAppSession),
    );
    window.localStorage.setItem(DESKTOP_HINT_SEEN_KEY, "1");
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts. The
    // destination still opens normally; it simply keeps the standard shell.
  }
}

export function openDesktopApp(
  item: Pick<SidebarItem, "id" | "path" | "label" | "icon">,
): void {
  prepareDesktopApp(item);
  go(item.path);
}

export function readDesktopApp(): DesktopAppSession | null {
  try {
    const raw = storage()?.getItem(DESKTOP_APP_SESSION_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<DesktopAppSession>;
    if (
      typeof value.id !== "string" ||
      typeof value.label !== "string" ||
      typeof value.icon !== "string"
    ) {
      return null;
    }
    return { id: value.id, label: value.label, icon: value.icon };
  } catch {
    return null;
  }
}

export function closeDesktopApp(): void {
  clearDesktopApp();
  go("/dashboard");
}

export function clearDesktopApp(): void {
  try {
    storage()?.removeItem(DESKTOP_APP_SESSION_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

export function hasSeenDesktopHint(): boolean {
  try {
    return window.localStorage.getItem(DESKTOP_HINT_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}
